import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import "./new-password.css";
import { useNavigate } from "react-router";

const NewPassword = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    newPassword: "",
    confirmNewPassword: "",
  });
  const [errors, setErrors] = useState({});
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmNewPassword, setShowConfirmNewPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [apiSuccess, setApiSuccess] = useState(null);
  const newPasswordRef = useRef(null);

  // Extract key and login from URL query parameters
  const urlParams = new URLSearchParams(window.location.search);
  const resetKey = urlParams.get("key") || "";
  const login = urlParams.get("login") || "";

  const API_ENDPOINT = "/wp-json/dbsb/v1/reset-password";

  useEffect(() => {
    // Check if key or login is missing and set an error
    if (!resetKey || !login) {
      setApiError(
        "Invalid or missing reset link. Please request a new password reset."
      );
    }

    const fromReset = urlParams.get("fromReset");
    if (fromReset === "true" && resetKey && login) {
      setTimeout(() => {
        if (newPasswordRef.current) {
          newPasswordRef.current.focus();
        }
      }, 100);
    }
  }, [resetKey, login]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    validateField(name, value);
  };

  const validateField = (name, value) => {
    const newErrors = { ...errors };
    switch (name) {
      case "newPassword":
        newErrors.newPassword = !value
          ? "New Password is required"
          : !/^(?=.*[A-Z])(?=.*[\W_]).{8,}$/.test(value)
          ? "Password must have at least 8 characters, one uppercase letter, and one symbol"
          : "";
        break;
      case "confirmNewPassword":
        newErrors.confirmNewPassword = !value
          ? "Confirm New Password is required"
          : value !== formData.newPassword
          ? "Passwords do not match"
          : "";
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const allTouched = {
      newPassword: true,
      confirmNewPassword: true,
    };
    Object.keys(formData).forEach((field) =>
      validateField(field, formData[field])
    );

    // Validate form data and URL parameters
    if (
      !resetKey ||
      !login ||
      Object.values(formData).some((value) => !value) ||
      Object.values(errors).some((error) => error)
    ) {
      return;
    }

    setIsLoading(true);
    setApiError(null);
    setApiSuccess(null);

    try {
      const response = await fetch(API_ENDPOINT, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          key: resetKey,
          login: login,
          new_password: formData.newPassword,
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || "Password reset failed");
      }

      setApiSuccess("Password updated successfully! Redirecting to login...");
      setFormData({
        newPassword: "",
        confirmNewPassword: "",
      });
      setTimeout(() => {
        window.location.href = "/login";
      }, 2000);
    } catch (error) {
      setApiError(error.message);
      setTimeout(() => setApiError(null), 5000); // Clear error after 5 seconds
    } finally {
      setIsLoading(false);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0, y: -50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  };

  const formVariants = {
    hidden: { opacity: 0, x: -100 },
    visible: {
      opacity: 1,
      x: 0,
      transition: { duration: 0.6, ease: "easeOut", staggerChildren: 0.2 },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.4, ease: [0.6, -0.05, 0.01, 0.99] },
    },
  };

  const buttonVariants = {
    hover: {
      scale: 1.02,
      backgroundColor: "#d1d9e6",
      transition: { duration: 0.2 },
    },
    tap: { scale: 0.98 },
  };

  return (
    <motion.div
      className="new-password-container"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div className="new-password-card" variants={containerVariants}>
        <div className="logo">
          <a
            href="/"
            onClick={(e) => {
              e.preventDefault();
              navigate("/");
            }}
          >
            <img
              src={`${process.env.PUBLIC_URL}/assets/logo.svg`}
              alt="Direct by Syndicate Bio"
            />
          </a>
        </div>
        <motion.div
          className="new-password-content"
          variants={formVariants}
          initial="hidden"
          animate="visible"
        >
          <h2>Reset Your Password</h2>
          <p>Create a new password for your account</p>
          <motion.form
            onSubmit={handleSubmit}
            className="new-password-form"
            variants={formVariants}
            initial="hidden"
            animate="visible"
          >
            <motion.div
              variants={itemVariants}
              className="form-group password-group"
            >
              <label>New Password</label>
              <div className="password-wrapper">
                <input
                  type={showNewPassword ? "text" : "password"}
                  name="newPassword"
                  value={formData.newPassword}
                  onChange={handleChange}
                  placeholder="Enter your new password"
                  ref={newPasswordRef}
                />
                <span
                  className={`toggle-password ${
                    formData.newPassword ? "visible" : "hidden"
                  }`}
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? "Hide" : "Show"}
                </span>
              </div>
              {errors.newPassword && (
                <span className="error">{errors.newPassword}</span>
              )}
            </motion.div>
            <motion.div
              variants={itemVariants}
              className="form-group password-group"
            >
              <label>Confirm New Password</label>
              <div className="password-wrapper">
                <input
                  type={showConfirmNewPassword ? "text" : "password"}
                  name="confirmNewPassword"
                  value={formData.confirmNewPassword}
                  onChange={handleChange}
                  placeholder="Re-enter your new password"
                />
                <span
                  className={`toggle-password ${
                    formData.confirmNewPassword ? "visible" : "hidden"
                  }`}
                  onClick={() =>
                    setShowConfirmNewPassword(!showConfirmNewPassword)
                  }
                >
                  {showConfirmNewPassword ? "Hide" : "Show"}
                </span>
              </div>
              {errors.confirmNewPassword && (
                <span className="error">{errors.confirmNewPassword}</span>
              )}
            </motion.div>
            {apiError && (
              <motion.p className="error-message" variants={itemVariants}>
                {apiError}
              </motion.p>
            )}
            {apiSuccess && (
              <motion.p className="success-message" variants={itemVariants}>
                {apiSuccess}
              </motion.p>
            )}
            <motion.div variants={itemVariants}>
              <motion.button
                type="submit"
                className={`continue-button ${
                  isLoading ||
                  !resetKey ||
                  !login ||
                  Object.values(formData).some((value) => !value) ||
                  Object.values(errors).some((error) => error)
                    ? "disabled"
                    : ""
                }`}
                disabled={
                  isLoading ||
                  !resetKey ||
                  !login ||
                  Object.values(formData).some((value) => !value) ||
                  Object.values(errors).some((error) => error)
                }
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                {isLoading ? "Saving..." : "Reset Password"}
              </motion.button>
            </motion.div>
          </motion.form>
          <div className="login-section">
            <div className="or-line">
              <ul></ul>
              <p className="login-link">OR</p>
              <ul></ul>
            </div>
            <a
              href="/login"
              className="login-link"
              onClick={(e) => {
                e.preventDefault();
                navigate("/login");
              }}
            >
              Back to Login
            </a>
          </div>
        </motion.div>
      </motion.div>
    </motion.div>
  );
};

export default NewPassword;
